import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// Placeholder images for luxury fabric appeal
const romanImages = {
  hero: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80",
  detail: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=600&q=80",
  lifestyle: "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=600&q=80",
};

function RomanBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-stone-900">
      <Helmet>
        <title>Roman Blinds UK | Designer Fabric & Blackout Roman Blinds</title>
        <meta
          name="description"
          content="Elevate your decor with made-to-measure Roman blinds. Discover 2026's luxury fabric collections, velvet textures, and thermal linings at MF Blinds UK."
        />
        <meta name="keywords" content="Roman Blinds UK, Custom Fabric Blinds, Luxury Window Treatments, Velvet Roman Blinds, Thermal Lined Blinds, Modern Soft Furnishings" />
      </Helmet>

      {/* --- Section 1: Hero - Elegance and Sophistication --- */}
      <header className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div>
          <span className="text-stone-500 font-bold tracking-widest uppercase text-xs mb-3 block">The Luxury Fabric Collection</span>
          <h1 className="text-5xl md:text-7xl font-serif font-medium mb-6 leading-tight">
            Timeless <br /><span className="italic text-stone-400">Roman Blinds</span>
          </h1>
          <p className="text-xl text-stone-600 mb-8 leading-relaxed">
            Roman blinds are the ultimate choice for those who want the warmth of curtains with the clean lines of a blind. In 2026, <strong>textured fabrics</strong> and <strong>earthy tones</strong> are defining the modern British home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="bg-stone-800 text-white px-10 py-4 rounded-md font-bold hover:bg-stone-700 transition-all shadow-lg">
              Order Free Fabric Swatches
            </button>
            <Link to="/measuring-guide" className="px-10 py-4 border border-stone-300 rounded-md font-bold hover:bg-stone-50 transition text-center">
              Measuring Guide
            </Link>
          </div>
        </div>
        <div className="relative">
          <img 
            src={romanImages.hero} 
            alt="Soft grey Roman blinds with elegant folds in a designer lounge" 
            className="rounded-2xl shadow-2xl h-[550px] w-full object-cover"
          />
          <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md p-6 rounded-lg shadow-xl">
            <p className="text-sm font-bold uppercase text-stone-500">2026 Trend Alert</p>
            <p className="text-lg font-serif italic text-stone-800">Sustainable Linen & Organic Cottons</p>
          </div>
        </div>
      </header>

      {/* --- Section 2: Features - Why Fabric Matters --- */}
      <section className="py-16 border-y border-stone-100 mb-24 grid md:grid-cols-3 gap-12">
        <div className="text-center">
          <div className="text-3xl mb-4">🧵</div>
          <h3 className="text-xl font-bold mb-3">Premium Linings</h3>
          <p className="text-stone-600 text-sm">Choose between standard, dim-out, or thermal blackout linings to customize your light and heat control.</p>
        </div>
        <div className="text-center">
          <div className="text-3xl mb-4">🌬️</div>
          <h3 className="text-xl font-bold mb-3">Thermal Efficiency</h3>
          <p className="text-stone-600 text-sm">The thick fabric layers of Roman blinds act as a natural insulator, keeping your energy bills low year-round.</p>
        </div>
        <div className="text-center">
          <div className="text-3xl mb-4">✨</div>
          <h3 className="text-xl font-bold mb-3">Hand-Finished Quality</h3>
          <p className="text-stone-600 text-sm">Every blind is made to measure with invisible stitching for a seamless, high-end "boutique hotel" finish.</p>
        </div>
      </section>

      {/* --- Section 3: Style Guide & Use Cases --- */}
      <div className="grid lg:grid-cols-2 gap-16 mb-24 items-center">
        <img src={romanImages.lifestyle} alt="Roman blinds in a cozy bedroom" className="rounded-3xl shadow-lg order-2 lg:order-1" />
        <div className="order-1 lg:order-2">
          <h2 className="text-3xl font-bold mb-6">Perfect for Bedrooms & Lounges</h2>
          <p className="text-lg text-stone-700 mb-6">
            Roman blinds are particularly effective in rooms where you want to introduce <strong>color, texture, and pattern</strong>. Unlike hard-surface blinds, the soft folds add a layer of acoustic comfort—reducing echoes in rooms with hard flooring.
          </p>
          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <span className="text-stone-400">✔</span> 
              <span><strong>Blackout Lined:</strong> Ideal for nurseries and bedrooms.</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="text-stone-400">✔</span> 
              <span><strong>Interlined:</strong> For extra "heft" and superior insulation.</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="text-stone-400">✔</span> 
              <span><strong>Easy-Operation:</strong> Available with traditional cord or modern motorization.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* --- Section 4: SEO FAQ --- */}
      <section className="bg-stone-50 p-12 rounded-3xl mb-24">
        <h2 className="text-3xl font-bold mb-10 text-center">Roman Blinds Expert Advice</h2>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h4 className="font-bold text-lg mb-2">Can Roman blinds be cleaned?</h4>
            <p className="text-stone-600">While most Roman blinds are dry-clean only, regular light vacuuming with a brush attachment will keep your fabrics looking fresh for years.</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h4 className="font-bold text-lg mb-2">Inside vs. Outside Recess: Which is better?</h4>
            <p className="text-stone-600">Fitting Roman blinds <strong>outside</strong> the recess allows more light into the room when they are raised, as the fabric stack doesn't block the glass. Fitting <strong>inside</strong> gives a neater, more tailored look.</p>
          </div>
        </div>
      </section>

      {/* --- Section 5: Internal Link Navigation --- */}
      <footer className="text-center">
        <h2 className="text-2xl font-bold mb-8 italic">Complement Your Interior</h2>
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          <Link to="/curtains" className="px-8 py-3 bg-stone-100 rounded-full hover:bg-stone-200 transition">Matching Curtains</Link>
          <Link to="/blackout-blinds" className="px-8 py-3 bg-stone-100 rounded-full hover:bg-stone-200 transition">Blackout Options</Link>
          <Link to="/wooden-blinds" className="px-8 py-3 bg-stone-100 rounded-full hover:bg-stone-200 transition">Wooden Venetian Blinds</Link>
        </div>
        
        <div className="bg-stone-900 text-white p-12 rounded-2xl">
          <h3 className="text-3xl font-bold mb-4">Ready for a Luxury Upgrade?</h3>
          <p className="mb-8 opacity-80 max-w-xl mx-auto">Browse our 2026 collection of over 300 fabrics, from lush velvets to sustainable organic linens.</p>
          <button className="bg-white text-stone-900 px-12 py-4 rounded-full font-bold hover:scale-105 transition-transform shadow-2xl">
            Order Free Samples
          </button>
        </div>
      </footer>
    </div>
  );
}

export default RomanBlinds;